// ── Clan Panel ── Full-screen overlay for clan management
// Shows current clan info, member list, create/invite/leave/kick/promote actions.
// Fetches from /api/clan, /api/clans, and mutation endpoints.

import { getToken } from '../auth/token-store';

const ACCENT = '#e94560';
const BG_DARK = 'rgba(0,0,0,0.92)';
const FONT = '"Courier New", monospace';
const MOBILE_FRIENDLY_4COL_GRID =
  '1fr minmax(60px, auto) minmax(60px, auto) minmax(60px, auto)';

// ── Types matching server JSON shapes ──

export interface ClanInfo {
  id: string;
  name: string;
  tag: string;
  owner_id: string;
  created_at: string;
  max_members: number;
}

export interface ClanMemberInfo {
  clan_id: string;
  account_id: string;
  display_name?: string;
  displayName?: string;
  role: 'owner' | 'admin' | 'member';
  joined_at: string;
}

export interface ClanSummary {
  id: string;
  name: string;
  tag: string;
  member_count: number;
}

export interface ClanAnnouncement {
  text: string;
  date: string;
}

// ── View state ──

type ClanView = 'list' | 'detail' | 'create';

export class ClanPanel {
  private overlay: HTMLElement;
  private contentEl: HTMLElement;
  private visible = false;
  private apiBaseUrl: string;
  private onBack: (() => void) | null = null;
  private abortController = new AbortController();
  /**
   * UI-M4: render-scope AbortController for per-render listeners on action
   * buttons, list rows, form-field inputs, and showPromptDialog. Recycled at
   * the top of render() so old DOM closures are released.
   */
  private renderController = new AbortController();
  private activeDialog: HTMLElement | null = null;

  // Data
  private currentClan: ClanInfo | null = null;
  private currentRole: string | null = null;
  private members: ClanMemberInfo[] = [];
  private clanList: ClanSummary[] = [];
  private view: ClanView = 'list';
  private viewClanId: string | null = null;
  private errorMessage = '';
  private memberSearchQuery = '';
  private announcement: ClanAnnouncement | null = {
    text: 'Weekend scrim blocks open for registration.',
    date: new Date().toISOString(),
  };

  constructor(apiBaseUrl: string) {
    this.apiBaseUrl = apiBaseUrl.replace(/\/$/, '');

    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: BG_DARK,
      zIndex: '58',
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      color: '#ffffff',
      userSelect: 'none',
    });

    this.contentEl = document.createElement('div');
    Object.assign(this.contentEl.style, {
      width: 'min(640px, calc(100vw - 24px))',
      maxHeight: '80vh',
      border: `2px solid ${ACCENT}40`,
      borderRadius: '8px',
      background: 'rgba(26,26,46,0.98)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    });
    this.overlay.appendChild(this.contentEl);

    // Click outside to close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.hide();
    }, { signal: this.abortController.signal });

    document.body.appendChild(this.overlay);
    document.addEventListener('keydown', (e) => this.handleKeyDown(e), {
      signal: this.abortController.signal,
    });
  }

  setOnBack(callback: (() => void) | null): void {
    this.onBack = callback;
  }

  setAnnouncement(announcement: ClanAnnouncement): void {
    this.announcement = { ...announcement };
    if (this.visible) this.render();
  }

  async show(): Promise<void> {
    if (this.visible) return;
    this.visible = true;
    this.overlay.style.display = 'flex';
    this.errorMessage = '';
    // Load the player's clan first, then fall back to list
    await this.loadPlayerClan();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.closeDialog();
    this.overlay.style.display = 'none';
    this.onBack?.();
  }

  isOpen(): boolean {
    return this.visible;
  }

  dispose(): void {
    this.abortController.abort();
    this.renderController.abort();
    this.closeDialog();
    this.overlay.remove();
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.visible) {
      e.preventDefault();
      e.stopPropagation();
      if (this.activeDialog) {
        this.closeDialog();
        return;
      }
      this.hide();
    }
  }

  private closeDialog(): void {
    if (!this.activeDialog) return;
    this.activeDialog.remove();
    this.activeDialog = null;
  }

  private createDialogShell(title: string): { card: HTMLElement; body: HTMLElement; actions: HTMLElement } {
    this.closeDialog();

    const card = document.createElement('div');
    Object.assign(card.style, {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 'min(420px, calc(100vw - 48px))',
      background: 'rgba(16,16,26,0.98)',
      border: `1px solid ${ACCENT}60`,
      borderRadius: '8px',
      boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
      padding: '18px',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      zIndex: '59',
      userSelect: 'text',
    });

    const titleEl = document.createElement('div');
    Object.assign(titleEl.style, {
      color: '#fff',
      fontSize: '13px',
      letterSpacing: '2px',
      textTransform: 'uppercase',
      fontWeight: 'bold',
    });
    titleEl.textContent = title;
    card.appendChild(titleEl);

    const body = document.createElement('div');
    Object.assign(body.style, {
      color: '#bbb',
      fontSize: '12px',
      lineHeight: '1.6',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    });
    card.appendChild(body);

    const actions = document.createElement('div');
    Object.assign(actions.style, {
      display: 'flex',
      gap: '8px',
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
    });
    card.appendChild(actions);

    this.overlay.appendChild(card);
    this.activeDialog = card;
    return { card, body, actions };
  }

  private showConfirmDialog(
    title: string,
    message: string,
    onConfirm: () => void,
    confirmLabel = 'CONFIRM',
  ): void {
    const { body, actions } = this.createDialogShell(title);
    const messageEl = document.createElement('div');
    messageEl.textContent = message;
    body.appendChild(messageEl);

    const cancelBtn = this.createBtn('CANCEL', () => this.closeDialog());
    actions.appendChild(cancelBtn);

    const confirmBtn = this.createBtn(confirmLabel, () => {
      this.closeDialog();
      onConfirm();
    });
    confirmBtn.style.borderColor = 'rgba(255,82,82,0.4)';
    confirmBtn.style.color = '#ff5252';
    actions.appendChild(confirmBtn);
  }

  private showPromptDialog(
    title: string,
    placeholder: string,
    submitLabel: string,
    onSubmit: (value: string) => void,
  ): void {
    const { body, actions } = this.createDialogShell(title);
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = placeholder;
    Object.assign(input.style, {
      padding: '10px 12px',
      background: 'rgba(0,0,0,0.35)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: '3px',
      color: '#fff',
      fontSize: '13px',
      fontFamily: FONT,
      outline: 'none',
    });
    body.appendChild(input);

    const cancelBtn = this.createBtn('CANCEL', () => this.closeDialog());
    actions.appendChild(cancelBtn);

    const submit = () => {
      const value = input.value.trim();
      if (!value) return;
      this.closeDialog();
      onSubmit(value);
    };

    const submitBtn = this.createBtn(submitLabel, submit);
    submitBtn.style.background = 'rgba(233,69,96,0.15)';
    submitBtn.style.borderColor = `${ACCENT}60`;
    actions.appendChild(submitBtn);

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    }, { signal: this.renderController.signal });
    input.focus();
  }

  // ── API helpers ──

  private authHeaders(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  private async apiFetch(path: string, init?: RequestInit): Promise<Response> {
    return fetch(`${this.apiBaseUrl}${path}`, {
      ...init,
      headers: { ...this.authHeaders(), ...(init?.headers ?? {}) },
    });
  }

  // ── Data loaders ──

  private async loadPlayerClan(): Promise<void> {
    try {
      // We don't have a player_id on the client directly, so try fetching
      // clan list and also try to get "my clan" via a cookie/token-based approach.
      // The server uses GetAccountID from auth context, so GET /api/clan?player_id=me
      // won't work. Instead, load the list view.
      this.view = 'list';
      await this.loadClanList();
    } catch {
      this.view = 'list';
      this.render();
    }
  }

  private async loadClanList(): Promise<void> {
    try {
      const res = await this.apiFetch('/api/clans');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      this.clanList = data.clans ?? [];
      this.errorMessage = '';
    } catch (err) {
      console.error('[ClanPanel] Failed to load clans:', err);
      this.clanList = [];
      this.errorMessage = 'Failed to load clan list';
    }
    this.view = 'list';
    this.render();
  }

  private async loadClanDetail(clanId: string): Promise<void> {
    try {
      const res = await this.apiFetch(`/api/clan?id=${encodeURIComponent(clanId)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (this.viewClanId !== clanId) this.memberSearchQuery = '';
      this.currentClan = data.clan ?? null;
      this.members = data.members ?? [];
      this.currentRole = data.role ?? null;
      this.viewClanId = clanId;
      this.errorMessage = '';
    } catch (err) {
      console.error('[ClanPanel] Failed to load clan detail:', err);
      this.currentClan = null;
      this.members = [];
      this.errorMessage = 'Failed to load clan details';
    }
    this.view = 'detail';
    this.render();
  }

  // ── Mutation actions ──

  private async createClan(name: string, tag: string): Promise<void> {
    try {
      const res = await this.apiFetch('/api/clan/create', {
        method: 'POST',
        body: JSON.stringify({ name, tag }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Create failed' }));
        this.errorMessage = (err as { error?: string }).error ?? 'Create failed';
        this.render();
        return;
      }
      const data = await res.json();
      const clan = data.clan as ClanInfo;
      this.errorMessage = '';
      await this.loadClanDetail(clan.id);
    } catch {
      this.errorMessage = 'Network error creating clan';
      this.render();
    }
  }

  private async invitePlayer(clanId: string, inviteeId: string): Promise<void> {
    try {
      const res = await this.apiFetch('/api/clan/invite', {
        method: 'POST',
        body: JSON.stringify({ clan_id: clanId, invitee_id: inviteeId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Invite failed' }));
        this.errorMessage = (err as { error?: string }).error ?? 'Invite failed';
      } else {
        this.errorMessage = 'Invite sent!';
      }
    } catch {
      this.errorMessage = 'Network error sending invite';
    }
    this.render();
  }

  private async acceptInvite(inviteId: string): Promise<void> {
    try {
      const res = await this.apiFetch('/api/clan/accept', {
        method: 'POST',
        body: JSON.stringify({ invite_id: inviteId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Accept failed' }));
        this.errorMessage = (err as { error?: string }).error ?? 'Accept failed';
        this.render();
        return;
      }
      this.errorMessage = '';
      await this.loadClanList();
    } catch {
      this.errorMessage = 'Network error accepting invite';
      this.render();
    }
  }

  private async leaveClan(clanId: string): Promise<void> {
    try {
      const res = await this.apiFetch('/api/clan/leave', {
        method: 'POST',
        body: JSON.stringify({ clan_id: clanId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Leave failed' }));
        this.errorMessage = (err as { error?: string }).error ?? 'Leave failed';
        this.render();
        return;
      }
      this.errorMessage = '';
      await this.loadClanList();
    } catch {
      this.errorMessage = 'Network error leaving clan';
      this.render();
    }
  }

  private async kickMember(clanId: string, targetId: string): Promise<void> {
    try {
      const res = await this.apiFetch('/api/clan/kick', {
        method: 'POST',
        body: JSON.stringify({ clan_id: clanId, target_id: targetId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Kick failed' }));
        this.errorMessage = (err as { error?: string }).error ?? 'Kick failed';
      } else {
        this.errorMessage = '';
      }
    } catch {
      this.errorMessage = 'Network error kicking member';
    }
    await this.loadClanDetail(clanId);
  }

  private async promoteMember(clanId: string, targetId: string, role: string): Promise<void> {
    try {
      const res = await this.apiFetch('/api/clan/promote', {
        method: 'POST',
        body: JSON.stringify({ clan_id: clanId, target_id: targetId, role }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Promote failed' }));
        this.errorMessage = (err as { error?: string }).error ?? 'Promote failed';
      } else {
        this.errorMessage = '';
      }
    } catch {
      this.errorMessage = 'Network error promoting member';
    }
    await this.loadClanDetail(clanId);
  }

  private async disbandClan(clanId: string): Promise<void> {
    try {
      const res = await this.apiFetch('/api/clan/disband', {
        method: 'DELETE',
        body: JSON.stringify({ clan_id: clanId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Disband failed' }));
        this.errorMessage = (err as { error?: string }).error ?? 'Disband failed';
        this.render();
        return;
      }
      this.errorMessage = '';
      await this.loadClanList();
    } catch {
      this.errorMessage = 'Network error disbanding clan';
      this.render();
    }
  }

  // ── Rendering ──

  private render(): void {
    // Recycle the render-scope controller so per-render listeners on detached
    // DOM (action buttons, list rows, form fields) are released (UI-M4).
    this.renderController.abort();
    this.renderController = new AbortController();
    this.contentEl.innerHTML = '';

    // Header
    const header = this.createHeader('CLANS', this.view === 'list'
      ? 'Browse and manage clans'
      : this.view === 'create'
        ? 'Create a new clan'
        : `Clan details`);
    this.contentEl.appendChild(header);

    const announcement = this.createAnnouncementBanner();
    if (announcement) this.contentEl.appendChild(announcement);

    // Error bar
    if (this.errorMessage) {
      const errBar = document.createElement('div');
      Object.assign(errBar.style, {
        padding: '8px 16px',
        background: this.errorMessage.includes('sent') ? 'rgba(76,175,80,0.15)' : 'rgba(233,69,96,0.15)',
        color: this.errorMessage.includes('sent') ? '#4caf50' : ACCENT,
        fontSize: '12px',
        letterSpacing: '1px',
      });
      errBar.textContent = this.errorMessage;
      this.contentEl.appendChild(errBar);
    }

    // View-specific content
    const body = document.createElement('div');
    Object.assign(body.style, {
      flex: '1',
      overflowY: 'auto',
      padding: '16px',
    });

    switch (this.view) {
      case 'list':
        this.renderListView(body);
        break;
      case 'create':
        this.renderCreateView(body);
        break;
      case 'detail':
        this.renderDetailView(body);
        break;
    }

    this.contentEl.appendChild(body);

    // Footer
    const footer = document.createElement('div');
    Object.assign(footer.style, {
      padding: '12px',
      textAlign: 'center',
      fontSize: '11px',
      color: '#444',
      borderTop: '1px solid rgba(255,255,255,0.05)',
    });
    footer.textContent = '[ESC] Close';
    this.contentEl.appendChild(footer);
  }

  private createHeader(title: string, subtitle: string): HTMLElement {
    const header = document.createElement('div');
    Object.assign(header.style, {
      padding: '20px 24px 14px',
      borderBottom: `1px solid ${ACCENT}30`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    });

    const left = document.createElement('div');
    const titleEl = document.createElement('div');
    Object.assign(titleEl.style, {
      fontSize: '22px',
      fontWeight: 'bold',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      color: ACCENT,
    });
    titleEl.textContent = title;
    left.appendChild(titleEl);

    const subEl = document.createElement('div');
    Object.assign(subEl.style, {
      fontSize: '11px',
      color: '#555',
      letterSpacing: '2px',
      marginTop: '4px',
    });
    subEl.textContent = subtitle;
    left.appendChild(subEl);
    header.appendChild(left);

    // Nav buttons
    const nav = document.createElement('div');
    Object.assign(nav.style, { display: 'flex', gap: '8px' });

    if (this.view !== 'list') {
      const backBtn = this.createBtn('BACK', () => {
        void this.loadClanList();
      });
      nav.appendChild(backBtn);
    }

    if (this.view === 'list') {
      const createBtn = this.createBtn('CREATE CLAN', () => {
        this.view = 'create';
        this.errorMessage = '';
        this.render();
      });
      createBtn.style.background = 'rgba(233,69,96,0.15)';
      createBtn.style.borderColor = `${ACCENT}60`;
      nav.appendChild(createBtn);
    }

    const closeBtn = this.createBtn('CLOSE', () => this.hide());
    nav.appendChild(closeBtn);
    header.appendChild(nav);

    return header;
  }

  private createAnnouncementBanner(): HTMLElement | null {
    if (!this.announcement) return null;

    const banner = document.createElement('div');
    Object.assign(banner.style, {
      padding: '10px 16px',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      background: 'rgba(233,69,96,0.08)',
      display: 'flex',
      justifyContent: 'space-between',
      gap: '12px',
      alignItems: 'center',
      fontSize: '12px',
    });

    const textEl = document.createElement('div');
    Object.assign(textEl.style, {
      color: '#ddd',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    });
    textEl.textContent = this.announcement.text;
    banner.appendChild(textEl);

    const dateEl = document.createElement('div');
    Object.assign(dateEl.style, {
      color: '#777',
      fontSize: '10px',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      flexShrink: '0',
    });
    const date = new Date(this.announcement.date);
    dateEl.textContent = Number.isNaN(date.getTime()) ? this.announcement.date : date.toLocaleDateString();
    banner.appendChild(dateEl);

    return banner;
  }

  private createBtn(text: string, onClick: () => void): HTMLElement {
    const btn = document.createElement('button');
    btn.textContent = text;
    Object.assign(btn.style, {
      padding: '8px 14px',
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.14)',
      borderRadius: '3px',
      color: '#d2d2cb',
      cursor: 'pointer',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      fontSize: '11px',
      fontFamily: FONT,
    });
    const sig = this.renderController.signal;
    btn.addEventListener('mouseenter', () => {
      btn.style.borderColor = `${ACCENT}90`;
      btn.style.color = '#fff';
    }, { signal: sig });
    btn.addEventListener('mouseleave', () => {
      btn.style.borderColor = 'rgba(255,255,255,0.14)';
      btn.style.color = '#d2d2cb';
    }, { signal: sig });
    btn.addEventListener('click', onClick, { signal: sig });
    return btn;
  }

  // ── List view ──

  private renderListView(container: HTMLElement): void {
    if (this.clanList.length === 0) {
      const empty = document.createElement('div');
      Object.assign(empty.style, {
        color: '#666',
        textAlign: 'center',
        padding: '40px',
        fontSize: '13px',
      });
      empty.textContent = 'No clans found. Create one to get started!';
      container.appendChild(empty);
      return;
    }

    // Column headers
    const colHeader = document.createElement('div');
    Object.assign(colHeader.style, {
      display: 'grid',
      gridTemplateColumns: '80px 1fr 100px 80px',
      padding: '8px 12px',
      fontSize: '10px',
      color: '#666',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
    });
    colHeader.innerHTML = '<div>TAG</div><div>NAME</div><div>MEMBERS</div><div></div>';
    container.appendChild(colHeader);

    for (const clan of this.clanList) {
      const row = document.createElement('div');
      Object.assign(row.style, {
        display: 'grid',
        gridTemplateColumns: '80px 1fr 100px 80px',
        padding: '10px 12px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        alignItems: 'center',
        fontSize: '13px',
        cursor: 'pointer',
        transition: 'background 0.1s',
      });
      row.addEventListener('mouseenter', () => { row.style.background = 'rgba(255,255,255,0.03)'; }, { signal: this.renderController.signal });
      row.addEventListener('mouseleave', () => { row.style.background = 'transparent'; }, { signal: this.renderController.signal });

      // Tag
      const tagEl = document.createElement('div');
      tagEl.style.color = ACCENT;
      tagEl.style.fontWeight = 'bold';
      tagEl.textContent = `[${clan.tag}]`;
      row.appendChild(tagEl);

      // Name
      const nameEl = document.createElement('div');
      nameEl.style.color = '#fff';
      nameEl.textContent = clan.name;
      row.appendChild(nameEl);

      // Members
      const membersEl = document.createElement('div');
      membersEl.style.color = '#888';
      membersEl.style.textAlign = 'center';
      membersEl.textContent = `${clan.member_count}`;
      row.appendChild(membersEl);

      // View button
      const viewBtn = this.createBtn('VIEW', () => {
        void this.loadClanDetail(clan.id);
      });
      viewBtn.style.padding = '4px 10px';
      viewBtn.style.fontSize = '10px';
      row.appendChild(viewBtn);

      container.appendChild(row);
    }
  }

  // ── Create view ──

  private renderCreateView(container: HTMLElement): void {
    const form = document.createElement('div');
    Object.assign(form.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      maxWidth: '400px',
      margin: '20px auto',
    });

    // Name field
    const nameGroup = this.createFormField('Clan Name', 'text', 'e.g. Tactical Ops');
    form.appendChild(nameGroup.wrapper);

    // Tag field
    const tagGroup = this.createFormField('Tag (max 5 chars)', 'text', 'e.g. TOPS');
    (tagGroup.input as HTMLInputElement).maxLength = 5;
    form.appendChild(tagGroup.wrapper);

    // Submit
    const submitBtn = this.createBtn('CREATE', () => {
      const name = (nameGroup.input as HTMLInputElement).value.trim();
      const tag = (tagGroup.input as HTMLInputElement).value.trim().toUpperCase();
      if (!name || !tag) {
        this.errorMessage = 'Name and tag are required';
        this.render();
        return;
      }
      void this.createClan(name, tag);
    });
    submitBtn.style.background = 'rgba(233,69,96,0.15)';
    submitBtn.style.borderColor = `${ACCENT}60`;
    submitBtn.style.padding = '12px 20px';
    submitBtn.style.fontSize = '13px';
    submitBtn.style.alignSelf = 'flex-start';
    form.appendChild(submitBtn);

    container.appendChild(form);
  }

  // ── Detail view ──

  private renderDetailView(container: HTMLElement): void {
    if (!this.currentClan) {
      const empty = document.createElement('div');
      empty.style.color = '#666';
      empty.style.textAlign = 'center';
      empty.style.padding = '40px';
      empty.textContent = 'Clan not found';
      container.appendChild(empty);
      return;
    }

    const clan = this.currentClan;

    // Clan info card
    const infoCard = document.createElement('div');
    Object.assign(infoCard.style, {
      padding: '16px',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '5px',
      background: 'rgba(255,255,255,0.03)',
      marginBottom: '16px',
    });

    const clanTitle = document.createElement('div');
    Object.assign(clanTitle.style, {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#fff',
      marginBottom: '4px',
    });
    clanTitle.textContent = `[${clan.tag}] ${clan.name}`;
    infoCard.appendChild(clanTitle);

    const clanMeta = document.createElement('div');
    clanMeta.style.fontSize = '11px';
    clanMeta.style.color = '#666';
    clanMeta.textContent = `Owner: ${clan.owner_id.slice(0, 8)}...  |  Max members: ${clan.max_members}  |  Created: ${new Date(clan.created_at).toLocaleDateString()}`;
    infoCard.appendChild(clanMeta);

    container.appendChild(infoCard);

    // Action bar
    const actionBar = document.createElement('div');
    Object.assign(actionBar.style, {
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      flexWrap: 'wrap',
    });

    // U-M-002: gate INVITE / DISBAND on viewer role.
    // - INVITE: owners + admins (officers) may invite.
    // - DISBAND: owners only.
    // currentRole comes from /api/clan (server uses `role` field with values
    // 'owner' | 'admin' | 'member'). If it's null (e.g. detail viewed without
    // membership context), both privileged actions are hidden — fail-closed is
    // safer than fail-open and the audit doc U-M-002 asked for this default.
    const isOwner = this.currentRole === 'owner';
    const isOfficer = isOwner || this.currentRole === 'admin';

    // Invite button — officers + owners only
    if (isOfficer) {
      const inviteBtn = this.createBtn('INVITE PLAYER', () => {
        this.showPromptDialog(
          'Invite Player',
          'Enter player account ID',
          'SEND INVITE',
          (inviteeId) => { void this.invitePlayer(clan.id, inviteeId); },
        );
      });
      actionBar.appendChild(inviteBtn);
    }

    // Leave button — visible to anyone viewing detail
    const leaveBtn = this.createBtn('LEAVE CLAN', () => {
      this.showConfirmDialog(
        'Leave Clan',
        'Are you sure you want to leave this clan?',
        () => { void this.leaveClan(clan.id); },
        'LEAVE CLAN',
      );
    });
    leaveBtn.style.borderColor = 'rgba(255,82,82,0.4)';
    leaveBtn.style.color = '#ff5252';
    actionBar.appendChild(leaveBtn);

    // Disband — owner only
    if (isOwner) {
      const disbandBtn = this.createBtn('DISBAND', () => {
        this.showConfirmDialog(
          'Disband Clan',
          'This will permanently delete the clan. Are you sure?',
          () => { void this.disbandClan(clan.id); },
          'DISBAND',
        );
      });
      disbandBtn.style.borderColor = 'rgba(255,82,82,0.4)';
      disbandBtn.style.color = '#ff5252';
      actionBar.appendChild(disbandBtn);
    }

    container.appendChild(actionBar);

    // Member list header
    const memberHeader = document.createElement('div');
    Object.assign(memberHeader.style, {
      fontSize: '14px',
      fontWeight: 'bold',
      color: ACCENT,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      marginBottom: '8px',
    });
    memberHeader.textContent = `MEMBERS (${this.members.length})`;
    container.appendChild(memberHeader);

    const memberSearch = document.createElement('input');
    memberSearch.type = 'search';
    memberSearch.placeholder = 'Search members';
    memberSearch.value = this.memberSearchQuery;
    Object.assign(memberSearch.style, {
      width: '100%',
      boxSizing: 'border-box',
      padding: '9px 12px',
      marginBottom: '10px',
      background: 'rgba(0,0,0,0.35)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: '3px',
      color: '#fff',
      fontSize: '12px',
      fontFamily: FONT,
      outline: 'none',
    });
    container.appendChild(memberSearch);

    // Column headers
    const colHeader = document.createElement('div');
    Object.assign(colHeader.style, {
      display: 'grid',
      gridTemplateColumns: MOBILE_FRIENDLY_4COL_GRID,
      padding: '8px 12px',
      fontSize: '10px',
      color: '#666',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
    });
    colHeader.innerHTML = '<div>ACCOUNT</div><div>ROLE</div><div>JOINED</div><div>ACTIONS</div>';
    container.appendChild(colHeader);

    const membersBody = document.createElement('div');
    container.appendChild(membersBody);

    const getDisplayName = (member: ClanMemberInfo): string =>
      member.display_name ?? member.displayName ?? member.account_id;

    const renderMemberRows = () => {
      membersBody.innerHTML = '';
      const query = this.memberSearchQuery.trim().toLowerCase();
      const filteredMembers = query.length === 0
        ? this.members
        : this.members.filter((member) => getDisplayName(member).toLowerCase().includes(query));
      memberHeader.textContent = query.length === 0
        ? `MEMBERS (${this.members.length})`
        : `MEMBERS (${filteredMembers.length}/${this.members.length})`;

      if (filteredMembers.length === 0) {
        const empty = document.createElement('div');
        empty.style.color = '#666';
        empty.style.padding = '20px';
        empty.style.textAlign = 'center';
        empty.textContent = this.members.length === 0 ? 'No members' : 'No matching members';
        membersBody.appendChild(empty);
        return;
      }

      for (const member of filteredMembers) {
        const row = document.createElement('div');
        Object.assign(row.style, {
          display: 'grid',
          gridTemplateColumns: MOBILE_FRIENDLY_4COL_GRID,
          padding: '8px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          alignItems: 'center',
          fontSize: '13px',
        });

        // Account ID (truncated)
        const idEl = document.createElement('div');
        idEl.style.color = '#ccc';
        const displayName = getDisplayName(member);
        idEl.textContent = displayName.length > 12
          ? displayName.slice(0, 6) + '...' + displayName.slice(-4)
          : displayName;
        row.appendChild(idEl);

        // Role badge
        const roleEl = document.createElement('div');
        const roleColor = member.role === 'owner' ? '#e8b849' : member.role === 'admin' ? '#4caf50' : '#888';
        roleEl.style.color = roleColor;
        roleEl.style.fontWeight = 'bold';
        roleEl.style.fontSize = '11px';
        roleEl.style.textTransform = 'uppercase';
        roleEl.textContent = member.role;
        row.appendChild(roleEl);

        // Joined date
        const joinedEl = document.createElement('div');
        joinedEl.style.color = '#666';
        joinedEl.style.fontSize = '11px';
        joinedEl.textContent = new Date(member.joined_at).toLocaleDateString();
        row.appendChild(joinedEl);

        // Actions
        const actionsEl = document.createElement('div');
        Object.assign(actionsEl.style, { display: 'flex', gap: '4px' });

        if (member.role !== 'owner') {
          // Promote button
          const promoteBtn = this.createBtn('PROMOTE', () => {
            const newRole = member.role === 'member' ? 'admin' : 'owner';
            void this.promoteMember(clan.id, member.account_id, newRole);
          });
          promoteBtn.style.padding = '3px 8px';
          promoteBtn.style.fontSize = '9px';
          actionsEl.appendChild(promoteBtn);

          // Kick button
          const kickBtn = this.createBtn('KICK', () => {
            this.showConfirmDialog(
              'Kick Member',
              `Kick ${member.account_id.slice(0, 8)}... from the clan?`,
              () => { void this.kickMember(clan.id, member.account_id); },
              'KICK',
            );
          });
          kickBtn.style.padding = '3px 8px';
          kickBtn.style.fontSize = '9px';
          kickBtn.style.color = '#ff5252';
          kickBtn.style.borderColor = 'rgba(255,82,82,0.3)';
          actionsEl.appendChild(kickBtn);
        }

        row.appendChild(actionsEl);
        membersBody.appendChild(row);
      }
    };

    memberSearch.addEventListener('input', () => {
      this.memberSearchQuery = memberSearch.value;
      renderMemberRows();
    }, { signal: this.renderController.signal });
    renderMemberRows();
  }

  // ── Form helpers ──

  private createFormField(
    label: string,
    type: string,
    placeholder: string,
  ): { wrapper: HTMLElement; input: HTMLInputElement } {
    const wrapper = document.createElement('div');
    Object.assign(wrapper.style, { display: 'flex', flexDirection: 'column', gap: '6px' });

    const labelEl = document.createElement('label');
    Object.assign(labelEl.style, {
      fontSize: '11px',
      color: '#888',
      letterSpacing: '1px',
      textTransform: 'uppercase',
    });
    labelEl.textContent = label;
    wrapper.appendChild(labelEl);

    const input = document.createElement('input');
    input.type = type;
    input.placeholder = placeholder;
    Object.assign(input.style, {
      padding: '10px 12px',
      background: 'rgba(0,0,0,0.35)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: '3px',
      color: '#fff',
      fontSize: '13px',
      fontFamily: FONT,
      outline: 'none',
    });
    input.addEventListener('focus', () => { input.style.borderColor = `${ACCENT}60`; }, { signal: this.renderController.signal });
    input.addEventListener('blur', () => { input.style.borderColor = 'rgba(255,255,255,0.12)'; }, { signal: this.renderController.signal });
    wrapper.appendChild(input);

    return { wrapper, input };
  }
}
